import { useEffect } from 'react';
import { useNotificationStore, Notification } from '@flows.cash/core';
import { format } from 'date-fns';
import { Bell, Check, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useAuthStore as useCoreAuthStore } from '@flows.cash/core';

export default function NotificationsPage() {
  const { t } = useTranslation();
  const { 
    notifications, 
    isLoading, 
    syncNotifications, 
    markAsRead, 
    markAllAsRead,
    removeNotification,
    clearAll
  } = useNotificationStore();
  const { user: coreUser, isAuthenticated } = useCoreAuthStore();

  useEffect(() => {
    // Only sync notifications if we have an authenticated user
    if (isAuthenticated && coreUser) {
      console.log('Notifications page: Syncing notifications for user:', coreUser.id);
      syncNotifications();
    }
  }, [syncNotifications, coreUser, isAuthenticated]);

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleDelete = async (notificationId: string) => {
    await removeNotification(notificationId);
  };

  const handleClearAll = async () => {
    await clearAll();
  };

  const handleRefresh = async () => {
    if (coreUser) {
      await syncNotifications();
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'text-success dark:text-success bg-success/10';
      case 'error':
        return 'text-error dark:text-error bg-error/10';
      case 'warning':
        return 'text-warning dark:text-warning bg-warning/10';
      default:
        return 'text-info dark:text-info bg-info/10';
    }
  };

  const getNotificationTypeLabel = (notification: Notification) => {
    // First try to find a specific notification type translation based on the title
    // Compare with known patterns:
    if (notification.title.includes('Transaction')) {
      if (notification.title.includes('Added')) return t('notifications.transactions.added.title');
      if (notification.title.includes('Updated')) return t('notifications.transactions.updated.title');
      if (notification.title.includes('Deleted')) return t('notifications.transactions.deleted.title');
      if (notification.title.includes('Import')) return t('notifications.transactions.imported.title');
      if (notification.title.includes('Export')) return t('notifications.transactions.exportSuccess.title');
      return t('notifications.transactions.title', 'Transaction');
    }
    
    if (notification.title.includes('Account')) {
      if (notification.title.includes('Added')) return t('notifications.accounts.added.title');
      if (notification.title.includes('Updated')) return t('notifications.accounts.updated.title');
      if (notification.title.includes('Deleted')) return t('notifications.accounts.deleted.title');
      return t('notifications.accounts.title', 'Account');
    }
    
    if (notification.title.includes('Budget')) {
      if (notification.title.includes('Added')) return t('notifications.budget.added.title');
      if (notification.title.includes('Updated')) return t('notifications.budget.updated.title');
      if (notification.title.includes('Deleted')) return t('notifications.budget.deleted.title');
      if (notification.title.includes('Warning')) return t('notifications.budget.warning.title');
      if (notification.title.includes('Exceeded')) return t('notifications.budget.exceeded.title');
      return t('notifications.budget.title', 'Budget');
    }
    
    if (notification.title.includes('Goal')) {
      if (notification.title.includes('Added')) return t('notifications.savingGoals.added.title');
      if (notification.title.includes('Completed') || notification.title.includes('Achieved')) {
        return t('notifications.savingGoals.completed.title');
      }
      if (notification.title.includes('Progress')) return t('notifications.savingGoals.progress.title');
      return t('notifications.savingGoals.title', 'Saving Goal');
    }
    
    // Default to the basic notification type
    return t(`notifications.types.${notification.type}`);
  };

  const getNotificationTranslation = (notification: Notification) => {
    // Direct translation approach - simply use i18next to translate
    // This assumes the notification title and message are already the translation keys
    
    // Check if notification title is a translation key
    if (notification.title && notification.title.includes('notifications.')) {
      try {
        const translatedTitle = t(notification.title, notification.data as Record<string, string>);
        const translatedMessage = notification.message?.includes('notifications.') 
          ? t(notification.message, notification.data as Record<string, string>)
          : notification.message;
          
        return {
          title: translatedTitle as string,
          message: translatedMessage as string
        };
      } catch (error) {
        console.error('Translation error:', error);
      }
    }
    
    // If not a translation key or there was an error, return the original content
    return {
      title: notification.title,
      message: notification.message
    };
  };

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8 max-w-4xl">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
          {t('notifications.title')}
        </h1>
        
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="flex items-center justify-center gap-2 border-border dark:border-border-dark hover:bg-border/5 dark:hover:bg-border-dark/5 h-9 rounded-lg"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="hidden sm:inline">{t('notifications.refresh')}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllAsRead}
            className="flex items-center justify-center gap-2 border-border dark:border-border-dark hover:bg-border/5 dark:hover:bg-border-dark/5 h-9 rounded-lg"
          >
            <Check className="h-4 w-4" />
            <span className="hidden sm:inline">{t('notifications.markAllRead')}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAll}
            className="flex items-center justify-center gap-2 text-error hover:text-error hover:bg-error/10 border-border dark:border-border-dark col-span-2 sm:col-span-1 h-9 rounded-lg"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">{t('notifications.clearAll')}</span>
          </Button>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-sm p-4">
              <Skeleton className="h-6 w-1/4 mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))
        ) : notifications.length === 0 ? (
          <div className="text-center py-12 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border/25 dark:border-border-dark/80 rounded-xl shadow-sm">
            <Bell className="h-12 w-12 mx-auto text-text-tertiary mb-4" />
            <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary">{t('notifications.empty')}</h3>
            <p className="text-text-tertiary dark:text-text-dark-tertiary">{t('notifications.allCaughtUp', 'You\'re all caught up!')}</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={cn(
                "bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm p-3 sm:p-4 border border-border/25 dark:border-border-dark/80",
                !notification.is_read && "border-l-4 border-l-primary dark:border-l-primary-dark"
              )}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span
                      className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium",
                        getNotificationColor(notification.type)
                      )}
                    >
                      {getNotificationTypeLabel(notification)}
                    </span>
                    <span className="text-xs sm:text-sm text-text-tertiary dark:text-text-dark-tertiary">
                      {format(new Date(notification.created_at), 'MMM d, yyyy h:mm a')}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-medium text-text-primary dark:text-text-dark-primary mb-1">
                    {getNotificationTranslation(notification).title}
                  </h3>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary">{getNotificationTranslation(notification).message}</p>
                </div>
                
                <div className="flex justify-end items-center gap-2 mt-2 sm:mt-0 sm:ml-4">
                  {!notification.is_read && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMarkAsRead(notification.id)}
                      className="text-primary dark:text-primary-dark hover:text-primary hover:bg-primary/10 dark:hover:text-primary-dark dark:hover:bg-primary-dark/10 h-9 rounded-lg"
                    >
                      <Check className="h-4 w-4" />
                      <span className="ml-1 text-xs sm:hidden">{t('notifications.markAsRead')}</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(notification.id)}
                    className="text-error hover:text-error hover:bg-error/10 h-9 rounded-lg"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="ml-1 text-xs sm:hidden">{t('common.remove')}</span>
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
} 